import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';

import { Spinner } from '@apps/web/components/ui/spinner';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { VoiceQaCallDetailView } from '@apps/web/routes/_app/admin/voice-qa/components/voice_qa_call_detail_view';
import { voiceQaDetailSearchSchema } from '@apps/web/routes/_app/admin/voice-qa/search_params';
import { useShadowComparisonUiEnabled } from '@apps/web/routes/_app/admin/voice-qa/shadow_comparison_ui';
import { useVoiceCallListNavigation } from '@apps/web/routes/_app/admin/voice-qa/use_voice_call_list_navigation';
import {
    useVoiceQACall,
    useVoiceQATaxonomy,
} from '@apps/web/swr_data_fetching/admin_voice_qa_queries';
import { useAdminVoiceCallRecordingUrls } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const Route = createFileRoute('/_app/admin/voice-qa/$callId')({
    head: () => ({
        meta: [
            {
                title: 'Voice QA Call Detail | Phoebe Admin',
            },
        ],
    }),
    component: VoiceQACallDetailPage,
    validateSearch: voiceQaDetailSearchSchema,
});

function VoiceQACallDetailPage() {
    const { callId } = Route.useParams();
    const search = Route.useSearch();
    const router = useRouter();
    const listNav = useVoiceCallListNavigation(callId, search);
    const {
        data: call,
        isLoading,
        error,
        mutate: mutateCall,
    } = useVoiceQACall(callId);
    const { data: taxonomyData } = useVoiceQATaxonomy();
    const { enabled: shadowComparisonUiEnabled } =
        useShadowComparisonUiEnabled();
    const [isQueueingReview, setIsQueueingReview] = useState(false);
    const bucketLabelBySlug = useMemo(() => {
        const entries = (taxonomyData?.buckets ?? []).flatMap(
            (bucketDefinition) => [
                [bucketDefinition.slug, bucketDefinition.label] as const,
                ...(bucketDefinition.secondary_buckets ?? []).map(
                    (secondaryBucket) =>
                        [secondaryBucket.slug, secondaryBucket.label] as const,
                ),
            ],
        );
        return new Map(entries);
    }, [taxonomyData?.buckets]);

    const hasTurns = useMemo(() => {
        if (
            !call?.developer_transcript ||
            call.developer_transcript.length === 0
        ) {
            return false;
        }

        let assistantTurns = 0;
        let userTurns = 0;

        for (const entry of call.developer_transcript) {
            const kind = (entry as { readonly kind?: unknown } | null)?.kind;
            if (kind === 'assistant_text' || kind === 'bot_started_speaking') {
                assistantTurns += 1;
            } else if (kind === 'user_text') {
                userTurns += 1;
            }
        }

        return assistantTurns + userTurns > 0;
    }, [call?.developer_transcript]);

    const voiceCallIdsForRecordings = useMemo(() => {
        if (!call?.recording_url) {
            return null;
        }
        const isS3Url =
            call.recording_url.includes('s3.amazonaws.com') ||
            call.recording_url.startsWith('s3://');
        return isS3Url ? [call.id] : null;
    }, [call]);

    const {
        data: recordingUrls,
        error: recordingUrlsError,
        isLoading: isRecordingUrlLoading,
    } = useAdminVoiceCallRecordingUrls(voiceCallIdsForRecordings);
    const canQueueQaReview = hasTurns;

    const handleQueueQaReview = useCallback(async () => {
        setIsQueueingReview(true);
        try {
            await getApiClient().post({
                url: '/admin/voice-qa/queue-reviews',
                body: { call_ids: [callId] },
            });
            toast.success('Queued QA run', { persistNotification: false });
        } catch (err) {
            toast.error('Failed to queue QA run', {
                persistNotification: false,
            });
        } finally {
            setIsQueueingReview(false);
        }
    }, [callId]);

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Spinner size="xl" />
            </div>
        );
    }

    if (error || !call) {
        return (
            <div className="flex h-full items-center justify-center">
                <div className="text-red-500">
                    {error?.message ?? 'Voice call not found'}
                </div>
            </div>
        );
    }

    function handleBack() {
        // Stay inside Voice QA when a detail page was opened directly.
        if (router.history.canGoBack()) {
            router.history.back();
            return;
        }
        void router.navigate({ to: '/admin/voice-qa' });
    }

    return (
        <VoiceQaCallDetailView
            call={call}
            listNavigation={listNav}
            bucketLabelBySlug={bucketLabelBySlug}
            recordingUrls={recordingUrls}
            recordingUrlsError={recordingUrlsError}
            isRecordingUrlLoading={isRecordingUrlLoading}
            shadowComparisonUiEnabled={shadowComparisonUiEnabled}
            isQueueingReview={isQueueingReview}
            canQueueQaReview={canQueueQaReview}
            onQueueQaReview={handleQueueQaReview}
            onRatingSaved={() => void mutateCall()}
            onBack={handleBack}
        />
    );
}
